import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';

const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900';

export function ProBadge() {
  return (
    <span className="inline-block align-middle ml-2 text-[10px] leading-none font-bold uppercase tracking-wide px-1.5 py-1 rounded bg-blue-600 text-white">
      Pro<span className="sr-only"> (solo en el plan Pro)</span>
    </span>
  );
}

// Cada capacidad está verificada contra api/ (AiService, AiFase2, IaFase3Controller,
// OcrController, ImportarPdfController, ml/reentreno). Todas son solo Pro.
const CAPACIDADES = [
  { icon: '💬', t: 'Asistente que responde sobre tu dinero', d: 'Pregunta en español y responde con tus propios datos. Solo habla de temas de la app, verifica las cifras antes de mostrarlas y tiene 100 preguntas al mes. Las preguntas sugeridas no gastan de ese tope.' },
  { icon: '📝', t: 'Informe mensual narrado', d: 'Tu mes explicado en lenguaje natural, con recomendaciones. Los montos los calcula la app, no la IA.' },
  { icon: '🚨', t: 'Alertas con explicación', d: 'Cuando un gasto sale de lo normal, te dice en una frase qué pasó y por qué te avisamos.' },
  { icon: '📸', t: 'Escanear tickets', d: 'Foto del ticket y se llenan total, fecha y comercio. Primero lee el texto de la imagen; la IA solo ayuda si falta un dato.' },
  { icon: '📄', t: 'Estados de cuenta en PDF', d: 'Primero un lector por banco que concilia con el saldo; si no cuadra, la IA lo reintenta. Si aun así no cuadra, te avisamos para que lo revises.' },
  { icon: '🏷️', t: 'Categorización que aprende de ti', d: 'Tus reglas e historial primero; un modelo propio que se reentrena cada noche con lo que confirmas; IA solo para lo ambiguo.' },
  { icon: '🧾', t: 'Sugerencias de deducibilidad en CFDI', d: 'Reglas primero y IA en los casos dudosos, con tu régimen en cuenta. Siempre: confírmalo con tu contador.' },
  { icon: '✉️', t: 'Presupuesto sugerido por quincena', d: 'Propone montos por sobre a partir de tu historial. Tú lo revisas y decides si lo aplicas.' },
  { icon: '🎯', t: 'Coach de metas y reserva de impuestos', d: 'Convierte los montos que ya calculó la app en pasos concretos para tus metas y para apartar tus impuestos.' },
];

const CAPAS = [
  { n: '1', t: 'Tus reglas y tu historial', d: 'Tus reglas, palabras clave, historial y cálculos. Instantáneo y sin IA.' },
  { n: '2', t: 'Un modelo que aprende de ti', d: 'Se reentrena cada noche con las categorías que confirmas.' },
  { n: '3', t: 'IA, solo cuando hace falta', d: 'Para leer lo que no tiene formato (fotos, PDFs raros) o redactar una explicación.' },
];

const PRIVACIDAD = [
  'Los proveedores de IA procesan en EE. UU.',
  'Tus datos no se usan para entrenar modelos.',
  'Antes de enviar algo se quitan tu RFC, CURP, correos y números de cuenta o tarjeta.',
  'Las fotos se envían sin metadatos (ubicación, dispositivo).',
  'Las cifras salen siempre de tus datos; la IA no las inventa.',
];

function EjemploAsistente() {
  return (
    <figure className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 shadow-xl overflow-hidden" aria-label="Ejemplo ilustrativo de una conversación con el asistente">
      <figcaption className="px-5 py-3 bg-gradient-to-r from-blue-600 to-purple-700 text-white text-xs font-semibold">Ejemplo ilustrativo · datos de muestra</figcaption>
      <div className="p-5 space-y-3 text-sm">
        <p className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-blue-600 text-white px-4 py-2.5">¿Cuánto gasté en Uber desde julio?</p>
        <div className="max-w-[92%] rounded-2xl rounded-bl-sm bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-gray-100 px-4 py-2.5">
          {/* monto-ok: cifras de muestra de la landing pública, no son datos del usuario */}
          <p>Desde julio llevas <strong>$2,340</strong> en Uber, en 31 viajes.</p>
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">Basado en 31 movimientos de jul–sep</p>
        </div>
      </div>
    </figure>
  );
}

export default function SeccionIA() {
  return (
    <section id="ia" className="py-20 px-4 bg-slate-50 dark:bg-slate-800/40 scroll-mt-16">
      <div className="max-w-6xl mx-auto">
        <Reveal><div className="text-center mb-12">
          <span className="inline-block bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">IA · incluida en Pro</span>
          <h2 className="text-3xl sm:text-4xl font-black mb-3">IA que trabaja para ti, con tus números</h2>
          <p className="text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto">
            Primero tus reglas y tu historial; la IA solo entra cuando hace falta. Así las cifras salen siempre de tus datos y es fácil comprobarlas.
          </p>
        </div></Reveal>

        <ol className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          {CAPAS.map((c, i) => (
            <li key={c.n}>
              <Reveal delay={i * 90}>
                <div className="h-full flex gap-4 bg-white dark:bg-slate-800 rounded-2xl p-5 border border-gray-200 dark:border-slate-700">
                  <span aria-hidden="true" className="shrink-0 w-9 h-9 rounded-full bg-blue-600 text-white font-black flex items-center justify-center">{c.n}</span>
                  <div><h3 className="font-bold mb-1">{c.t}</h3><p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{c.d}</p></div>
                </div>
              </Reveal>
            </li>
          ))}
        </ol>

        <div className="grid lg:grid-cols-5 gap-8 items-start mb-12">
          <div className="lg:col-span-3">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CAPACIDADES.map((c, i) => (
                <li key={c.t}>
                  <Reveal delay={(i % 2) * 90}>
                    <div className="h-full bg-white dark:bg-slate-800 rounded-2xl p-5 border border-gray-200 dark:border-slate-700 lp-card">
                      <div aria-hidden="true" className="text-2xl mb-2">{c.icon}</div>
                      <h3 className="font-bold mb-1.5">{c.t}<ProBadge /></h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{c.d}</p>
                    </div>
                  </Reveal>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2 lg:sticky lg:top-20 space-y-6">
            <Reveal><EjemploAsistente /></Reveal>
            <Reveal delay={90}>
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 p-5">
                <h3 className="font-bold mb-3">IA y tu privacidad</h3>
                <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
                  {PRIVACIDAD.map(p => <li key={p} className="flex gap-2"><span aria-hidden="true" className="text-green-600 dark:text-green-400 shrink-0">✓</span>{p}</li>)}
                </ul>
                <Link to="/seguridad#ia" className={`inline-flex items-center min-h-[44px] mt-4 text-sm font-semibold text-blue-700 dark:text-blue-300 underline rounded ${FOCUS}`}>Más sobre IA y tus datos</Link>
              </div>
            </Reveal>
          </div>
        </div>

        <p className="text-center text-sm text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          El plan gratuito incluye 3 preguntas al mes al asistente; el resto de la IA es de Pro. Las sugerencias fiscales son orientativas, no asesoría: confírmalas con tu contador.
        </p>
      </div>
    </section>
  );
}
